import { ComponentProps } from "react";
import { Pill, PillWrapper, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import {
  SYSTEM_CALCULATE,
  SYSTEM_GENERATED_CAPABLE_FIELDS,
  kStaffingTarget,
  kDesiredHolders,
  restrictedInternalFields,
  kBudgetedHeadcount,
  kSalaryRollup,
  kCurrentPositionCost,
  kPositionBudgetRollup,
  kAllocation,
  kAssignmentStatus,
  kReservedFields
} from "@coreapp/constants";

type Props = {
  fieldName: string;
  fieldMapping?: { [key: string]: string };
  systemGeneratedFields?: Set<string>;
  labelProps?: Partial<ComponentProps<typeof Text>>;
  showLabel?: boolean;
  forceRender?: boolean;
};

export const isSystemGeneratedMappedField = (
  fieldName: string,
  fieldMapping?: { [key: string]: string }
): boolean => {
  return getAllSystemGeneratedMappedFields(fieldMapping).includes(fieldName);
};

export const getAllSystemGeneratedMappedFields = (fieldMapping?: {
  [key: string]: string;
}): string[] => {
  if (!fieldMapping) return [];

  // Names actively used as custom mapping targets are not system-generated,
  // even if they happen to coincide with another capable field's expected name.
  const customMappedValues = new Set(
    SYSTEM_GENERATED_CAPABLE_FIELDS.map(fieldId => fieldMapping[fieldId]).filter(
      (v): v is string => !!v && v !== "" && v !== SYSTEM_CALCULATE
    )
  );

  return SYSTEM_GENERATED_CAPABLE_FIELDS.reduce<string[]>((acc, fieldId) => {
    const mappedValue = fieldMapping[fieldId];
    if (!mappedValue || mappedValue === "" || mappedValue === SYSTEM_CALCULATE) {
      const expectedName = fieldId === kDesiredHolders ? kStaffingTarget : fieldId;
      if (!customMappedValues.has(expectedName)) {
        acc.push(expectedName);
      }
    }
    return acc;
  }, []);
};

export const systemGeneratedInternalFields = [
  ...restrictedInternalFields,
  ...kReservedFields,
  kBudgetedHeadcount,
  kSalaryRollup,
  kCurrentPositionCost,
  kPositionBudgetRollup,
  kAllocation,
  kAssignmentStatus
];

export const isSystemGeneratedInternalField = (fieldName: string): boolean =>
  systemGeneratedInternalFields.includes(fieldName);

export const getAllSystemGeneratedFieldNames = (fieldMapping?: {
  [key: string]: string;
}): string[] => {
  const mappedFields = getAllSystemGeneratedMappedFields(fieldMapping);
  const internalFields = systemGeneratedInternalFields;
  return [...new Set([...mappedFields, ...internalFields])];
};

export const isSystemGeneratedField = (
  fieldName: string,
  fieldMapping?: { [key: string]: string }
): boolean => {
  return (
    systemGeneratedInternalFields.includes(fieldName) ||
    getAllSystemGeneratedMappedFields(fieldMapping).includes(fieldName)
  );
};

export default function SystemGeneratedFieldPill({ fieldName, fieldMapping, forceRender }: Props) {
  return isSystemGeneratedField(fieldName, fieldMapping) || forceRender ? (
    <PillWrapper style={{ height: "100%", width: "100%", display: "flex", alignItems: "center" }}>
      <Pill label={appstr("System generated").toLowerCase()} />
    </PillWrapper>
  ) : null;
}
